'use client';

import type { UniversalAccessor } from '@/editor/core/universalAccessor';
import type { IEditableObject, ISceneTreeNode } from '@/editor/store/models';
import type * as ENGINE from '@gnsx/genesys.js';
import type * as THREE from 'three';

/** Plain data safe for `console` + forwarded dev logs (avoids live Three/engine graphs). */
export function actorLogRef(actor: ENGINE.Actor | null | undefined) {
  if (!actor) return null;
  return { uuid: actor.uuid, name: actor.name };
}

export function sceneComponentLogRef(c: ENGINE.SceneComponent | null | undefined) {
  if (!c) return null;
  return { uuid: c.uuid, name: c.name, ctor: c.constructor.name };
}

export function object3DLogRef(o: THREE.Object3D | null | undefined) {
  if (!o) return null;
  return { uuid: o.uuid, type: o.type, name: o.name };
}

export function threeMaterialLogRef(m: THREE.Material | null | undefined) {
  if (!m) return null;
  return { uuid: m.uuid, type: m.type, name: m.name };
}

export function universalAccessorForLog(a: UniversalAccessor) {
  return a.toString();
}

export function editableObjectOrNodeRef(object: IEditableObject | ISceneTreeNode) {
  const actor = object.asActor;
  return { id: object.id, name: object.name, actorUuid: actor?.uuid };
}

/**
 * `value` in property notifications — avoid logging the live instance; path/result are enough.
 */
export function notifyPropertyTargetRef(target: unknown): string | { uuid: string; name: string; ctor: string } {
  if (target == null) return String(target);
  if (typeof target !== 'object') return String(target);
  const t = target as { uuid?: unknown; name?: unknown; constructor?: { name?: unknown } };
  if (typeof t.uuid === 'string' && typeof t.name === 'string') {
    return {
      uuid: t.uuid,
      name: t.name,
      ctor: typeof t.constructor?.name === 'string' ? t.constructor.name : 'Object',
    };
  }
  return typeof t.constructor?.name === 'string' ? t.constructor.name : 'Object';
}
